import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import folderImg from "../../../assets/folderImage.svg";
import {
  archiveForm,
  createForm,
  deleteForm,
  selectAllForms,
  selectDraftForms,
  selectPendingSubmissions,
  selectPostSubmissions,
  selectPublishedForms,
  updateSubmissionStatus,
} from "../../../store/formsSlice";
import CreateFormModal from "./formBuilder/CreateFormModal";
import CompletedFormsList from "./formBuilder/CompletedFormsList";
import DraftsList from "./formBuilder/DraftsList";
import FormBuilder from "./formBuilder/FormBuilder";
import PendingRegistrationList from "./formBuilder/PendingRegistrationList";
import PostRegistrationList from "./formBuilder/PostRegistrationList";
import PublishSuccessModal from "./formBuilder/PublishSuccessModal";
import SuccessToast from "./formBuilder/SuccessToast";

const TABS = [
  { id: "create", label: "Create New Form" },
  { id: "completed", label: "Completed Forms" },
  { id: "pending", label: "Pending Registration" },
  { id: "post", label: "Post Registration" },
];

function EmptyLanding({ onCreate }) {
  return (
    <div className="flex flex-col items-center justify-start px-6 pb-20 pt-28 text-center">
      <img src={folderImg} alt="No forms" className="mb-6 h-40 w-40" />
      <h3 className="mb-2 text-lg font-medium text-gray-900">
        You currently have no active forms
      </h3>
      <p className="mb-5 text-gray-600">You&apos;re yet to create a form</p>
      <button
        type="button"
        onClick={onCreate}
        className="rounded-md bg-[#EAF524] px-6 py-2 font-medium text-black hover:bg-[#e0eb1f]"
      >
        Create a Form
      </button>
    </div>
  );
}

export default function SupplierRegistration() {
  const dispatch = useDispatch();
  const allForms = useSelector(selectAllForms);
  const drafts = useSelector(selectDraftForms);
  const published = useSelector(selectPublishedForms);
  const postSubmissions = useSelector(selectPostSubmissions);
  const pendingSubmissions = useSelector(selectPendingSubmissions);

  const [activeTab, setActiveTab] = useState("create");
  const [view, setView] = useState("landing");
  const [activeFormId, setActiveFormId] = useState(null);
  const [builderMode, setBuilderMode] = useState("create");
  const [requestSubmissionId, setRequestSubmissionId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [publishModalFormId, setPublishModalFormId] = useState(null);

  const activeForm = useMemo(
    () => allForms.find((f) => f.id === activeFormId) || null,
    [allForms, activeFormId]
  );

  const publishModalForm = useMemo(
    () => allForms.find((f) => f.id === publishModalFormId) || null,
    [allForms, publishModalFormId]
  );

  useEffect(() => {
    if (activeTab === "create" && view === "landing" && drafts.length > 0) {
      setView("drafts");
    }
  }, [activeTab, drafts.length, view]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const openCreateModal = () => setShowCreateModal(true);

  const handleCreate = ({ name, type }) => {
    const action = dispatch(createForm({ name, type }));
    const newId = action.payload.id;
    setShowCreateModal(false);
    setActiveFormId(newId);
    setBuilderMode("create");
    setActiveTab("create");
    setView("builder");
    setToast({
      title: "Success!",
      message: `${action.payload.typeLabel} successfully created.`,
    });
  };

  const handleDeleteDraft = (id) => {
    if (!window.confirm("Delete this draft form?")) return;
    dispatch(deleteForm(id));
    if (activeFormId === id) {
      setActiveFormId(null);
      setView(drafts.length <= 1 ? "landing" : "drafts");
    }
  };

  const leaveBuilder = () => {
    if (builderMode === "request" && activeFormId) {
      dispatch(deleteForm(activeFormId));
    }
    setActiveFormId(null);
    setBuilderMode("create");
    setRequestSubmissionId(null);
    if (builderMode === "request") {
      setActiveTab("post");
    } else {
      setView(drafts.length ? "drafts" : "landing");
    }
  };

  const handleRequestInfo = (item) => {
    const action = dispatch(
      createForm({
        name: `Request more information — ${item.company}`,
        type: "pre-registration",
        isRequest: true,
      })
    );
    setRequestSubmissionId(item.id);
    setActiveFormId(action.payload.id);
    setBuilderMode("request");
    setActiveTab("create");
    setView("builder");
  };

  const handleSendRequest = () => {
    if (requestSubmissionId) {
      dispatch(
        updateSubmissionStatus({
          id: requestSubmissionId,
          status: "under-review",
        })
      );
    }
    if (activeFormId) dispatch(deleteForm(activeFormId));
    setActiveFormId(null);
    setBuilderMode("create");
    setRequestSubmissionId(null);
    setActiveTab("post");
    setToast({
      title: "Request Sent",
      message: "The supplier has been asked for more information.",
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-7xl px-4">
          <h1 className="py-6 text-left text-2xl font-medium text-[#1D2739]">
            Supplier Registration
          </h1>
          <nav className="flex space-x-8">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  if (builderMode === "request" && activeFormId) {
                    dispatch(deleteForm(activeFormId));
                    setBuilderMode("create");
                    setRequestSubmissionId(null);
                    setActiveFormId(null);
                  }
                  setActiveTab(tab.id);
                  if (tab.id === "create") {
                    setView(drafts.length ? "drafts" : "landing");
                  }
                }}
                className={`border-b-2 px-1 py-2 text-sm font-medium ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-7xl">
        {activeTab === "create" && view === "landing" && (
          <EmptyLanding onCreate={openCreateModal} />
        )}

        {activeTab === "create" && view === "drafts" && (
          <DraftsList
            drafts={drafts}
            onCreateNew={openCreateModal}
            onEdit={(id) => {
              setActiveFormId(id);
              setBuilderMode("create");
              setView("builder");
            }}
            onDelete={handleDeleteDraft}
          />
        )}

        {activeTab === "create" && view === "builder" && activeForm && (
          <FormBuilder
            key={activeForm.id}
            form={activeForm}
            mode={builderMode}
            onBack={leaveBuilder}
            onPublished={(id) => setPublishModalFormId(id)}
            onSend={handleSendRequest}
          />
        )}

        {activeTab === "create" && view === "builder" && !activeForm && (
          <EmptyLanding onCreate={openCreateModal} />
        )}

        {activeTab === "completed" && (
          <CompletedFormsList
            forms={published}
            onArchive={(id) => dispatch(archiveForm(id))}
            onToast={setToast}
          />
        )}

        {activeTab === "pending" && (
          <PendingRegistrationList submissions={pendingSubmissions} />
        )}

        {activeTab === "post" && (
          <PostRegistrationList
            submissions={postSubmissions}
            onApprove={(item) => {
              dispatch(
                updateSubmissionStatus({ id: item.id, status: "approved" })
              );
              setToast({
                title: "Supplier Approved!",
                message: "Supplier can now be found under Management",
              });
            }}
            onReject={(item, reason) => {
              dispatch(
                updateSubmissionStatus({
                  id: item.id,
                  status: "rejected",
                  rejectReason: reason,
                })
              );
              setToast({
                title: "Supplier rejected",
                message: `${item.company} has been rejected.`,
              });
            }}
            onRequestInfo={handleRequestInfo}
          />
        )}
      </div>

      <CreateFormModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />

      <PublishSuccessModal
        open={!!publishModalForm}
        form={publishModalForm}
        onClose={() => {
          setPublishModalFormId(null);
          setActiveFormId(null);
          setBuilderMode("create");
          setActiveTab("completed");
        }}
      />

      <SuccessToast
        open={!!toast}
        title={toast?.title}
        message={toast?.message}
        onClose={() => setToast(null)}
      />
    </div>
  );
}
